import { Component } from '@theme/component';

const TOAST_VISIBLE_DURATION = 3500;

/**
 * Progressively enhances the "customer" newsletter signup form.
 *
 * The form is left to submit natively (full-page POST + redirect) rather
 * than being intercepted with `fetch` — Shopify's own spam protection on
 * this form can interpose a CAPTCHA-style challenge that only resolves
 * inside a real, top-level navigation, so an invisible background fetch just
 * stalls and then falls back to that same navigation anyway, with an extra
 * silent delay in front of it and no way to detect success early.
 *
 * Two small, purely additive enhancements instead:
 * - On submit, disable the button with a "Submitting…" label so a click
 *   registers as *something* happening immediately, and can't be repeated
 *   while the (possibly slow, possibly challenge-interrupted) navigation is
 *   in flight.
 * - On load, if the page is the redirect back from a successful submission
 *   (`form.posted_successfully?` — i.e. this block already contains its
 *   server-rendered success message) *and* that message is inside a dialog,
 *   show a brief, centred toast with the same text. The dialog itself is
 *   already closed by virtue of being a fresh page load; a toast is the only
 *   way the shopper would otherwise see that inline message at all.
 *
 * @extends Component
 */
class EmailSignupFormComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#maybeShowSuccessToast();
  }

  /**
   * Handles the form's submit event. Does not intervene in the actual
   * submission — see the class doc comment for why.
   */
  handleSubmit = () => {
    const button = this.querySelector('.email-signup__button');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    button.disabled = true;
    button.classList.add('email-signup__button--submitting');

    // Only present for text-labelled buttons — an icon-only button's markup
    // is an <svg>, which this would otherwise clobber.
    const label = button.dataset.submittingLabel;
    if (label) button.textContent = label;
  };

  /**
   * If this block is currently showing its server-rendered success message
   * (i.e. the page just loaded as the redirect from a successful submit) and
   * that message lives inside a dialog, surface it as a toast too — the
   * dialog is already closed on a fresh page load, so without this the
   * shopper would have no way to see it.
   */
  #maybeShowSuccessToast() {
    const successText = this.querySelector('.email-signup__message--success .email-signup__message-text');
    if (!successText) return;

    const dialog = this.#closestDialog();
    if (!dialog) return;

    this.#showToast(successText.textContent?.trim() ?? '');
  }

  /**
   * Finds the nearest ancestor dialog — the theme's `dialog.js` base class,
   * or anything else that duck-types the same `closeDialog` method — if any.
   * @returns {{ closeDialog: () => Promise<void> } | null}
   */
  #closestDialog() {
    /** @type {Element | null} */
    let node = this;

    while ((node = node.parentElement)) {
      if (typeof (/** @type {any} */ (node).closeDialog) === 'function') {
        return /** @type {any} */ (node);
      }
    }

    return null;
  }

  /**
   * Shows a brief, centred confirmation toast, cloned from the template
   * rendered alongside this block.
   * @param {string} message
   */
  #showToast(message) {
    if (!message) return;

    const template = document.querySelector('[data-email-signup-toast-template]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const toast = document.createElement('div');
    toast.className = 'email-signup-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    const bubble = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const text = bubble.querySelector('.email-signup-toast__text');
    if (text) text.textContent = message;

    toast.append(bubble);
    document.body.append(toast);

    requestAnimationFrame(() => toast.classList.add('email-signup-toast--visible'));

    setTimeout(() => {
      toast.classList.remove('email-signup-toast--visible');
      toast.addEventListener('transitionend', () => toast.remove(), { once: true });
      // Fallback in case the transition never fires (e.g. reduced motion).
      setTimeout(() => toast.remove(), 500);
    }, TOAST_VISIBLE_DURATION);
  }
}

if (!customElements.get('email-signup-form-component')) {
  customElements.define('email-signup-form-component', EmailSignupFormComponent);
}
